import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { AppLink } from '@frontend/settings-routing';
import { Breadcrumbs } from '@frontend/design-system';
import { APPOINTMENT_TABS, VIEW_KEYS } from '../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../hooks/use-appointment-analytics-v2-store';

interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export const AppointmentAnalyticsBreadcrumb = () => {
  const { comparingLocations, showingProspectList } = useSearch({ from: '/analytics/appointment' });
  const { activeTab, changeView } = useAppointmentAnalyticsV2ShallowStore('activeTab', 'changeView');

  const { t } = useTranslation('analytics');

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const baseCrumb: BreadcrumbItem[] = [
      { label: t('Analytics'), href: '/analytics/dashboard' },
      {
        label: t('Appointment Analytics'),
        href: `/analytics/appointment`,
        onClick: () => changeView(VIEW_KEYS.CHART),
      },
    ];

    if (comparingLocations) {
      return [
        ...baseCrumb,
        { label: activeTab === APPOINTMENT_TABS.UPCOMING ? t('Upcoming Appointments') : t('Past Appointments') },
      ];
    }

    if (showingProspectList) {
      return [...baseCrumb, { label: t('Prospect Patient List') }];
    }

    return [];
  }, [activeTab, t, comparingLocations, showingProspectList, changeView]);

  return (
    <Breadcrumbs>
      {breadcrumbs.map((crumb, index) =>
        'href' in crumb ? (
          <Breadcrumbs.Path key={index} as={AppLink} to={crumb.href} onClick={crumb.onClick}>
            {crumb.label}
          </Breadcrumbs.Path>
        ) : (
          <Breadcrumbs.Path
            key={index}
            onClick={crumb.onClick}
            style={crumb.onClick ? { cursor: 'pointer' } : undefined}
          >
            {crumb.label}
          </Breadcrumbs.Path>
        )
      )}
    </Breadcrumbs>
  );
};
